import React, { useContext, useEffect, useState } from 'react';
import Header from '../components/Header';
import UserAccessNav from '../components/UserAccessNav';
import { UserContext } from '../App';
import {
  getPushPublicKey,
  PushSubscriptionJson,
  registerPushSubscription,
  unregisterPushSubscription,
  UserRegistryRecord,
} from '../util/userAccessApi';
import {
  getBrowserPushSubscription,
  registerBrowserPush,
  unregisterBrowserPush,
} from '../util/pushNotifications';

function UserAccessNotifications() {
  const userCtx = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<PushSubscriptionJson | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);

  useEffect(() => {
    if (!userCtx.user) {
      setSubscription(null);
      setPublicKey(null);
      return;
    }

    let active = true;

    const loadPushState = () => {
      setLoading(true);
      setError(null);

      Promise.all([
        getPushPublicKey(),
        getBrowserPushSubscription(),
      ])
        .then(([response, currentSubscription]) => {
          if (!active) {
            return;
          }

          setPublicKey(response.enabled ? response.publicKey : '');
          setSubscription(currentSubscription);
        })
        .catch((e: Error) => {
          console.error('Failed to load push notification state!', e);
          if (!active) {
            return;
          }

          setError(e.message || 'Nepodarilo sa načítať Web Push konfiguráciu.');
        })
        .finally(() => {
          if (active) {
            setLoading(false);
          }
        });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadPushState();
      }
    };

    loadPushState();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', loadPushState);

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', loadPushState);
    };
  }, [userCtx.user]);

  const handleRegister = () => {
    if (!userCtx.user) {
      setError('Pre registráciu push notifikácií sa najskôr prihláste.');
      return;
    }

    setBusy(true);
    setError(null);
    setMessage(null);

    registerBrowserPush(publicKey || '')
      .then((nextSubscription) => registerPushSubscription(nextSubscription, navigator.userAgent).then(() => nextSubscription))
      .then((nextSubscription) => {
        setSubscription(nextSubscription);
        setMessage('Push notifikácie boli úspešne povolené.');
      })
      .catch((e: Error) => {
        console.error('Failed to register push notifications!', e);
        setError(e.message || 'Nepodarilo sa povoliť push notifikácie.');
      })
      .finally(() => setBusy(false));
  };

  const handleUnregister = () => {
    if (!userCtx.user) {
      setError('Pre správu push notifikácií sa najskôr prihláste.');
      return;
    }

    setBusy(true);
    setError(null);
    setMessage(null);

    unregisterBrowserPush()
      .then((existingSubscriptions) => {
        if (existingSubscriptions.length === 0) {
          return null;
        }
        return Promise.all(existingSubscriptions.map((existingSubscription) => unregisterPushSubscription(existingSubscription)));
      })
      .then(() => {
        setSubscription(null);
        setMessage('Push notifikácie boli odhlásené.');
      })
      .catch((e: Error) => {
        console.error('Failed to unregister push notifications!', e);
        setError(e.message || 'Nepodarilo sa odhlásiť push notifikácie.');
      })
      .finally(() => setBusy(false));
  };

  const configured = Boolean(publicKey && publicKey.trim().length > 0);

  return (
    <div className="flex flex-col min-h-0 h-full">
      <Header />
      <UserAccessNav />

      <div className="flex-1 overflow-auto p-6">
        <h1 className="text-2xl font-bold">Registrácia push notifikácií</h1>

        {!userCtx.user && <div className="mt-4">Neprihlásený!</div>}
        {userCtx.user && loading && <div className="mt-4">Načítavam...</div>}
        {userCtx.user && error && <div className="mt-4 text-red-600">{error}</div>}
        {userCtx.user && !loading && !error && (
          <div className="mt-4 max-w-3xl space-y-4">
            <p>
              Táto stránka zaregistruje zariadenie na prijímanie upozornení pri novom zázname prístupu.
            </p>

            {!configured && (
              <div className="rounded border border-amber-300 bg-amber-50 p-4">
                Web Push ešte nie je nakonfigurovaný na backend-e.
              </div>
            )}

            {subscription && (
              <div className="rounded border border-green-300 bg-green-50 p-4">
                Toto zariadenie už má aktívne Web Push odoberanie.
              </div>
            )}

            {message && <div className="rounded border border-green-300 bg-green-50 p-4">{message}</div>}
            {error && <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">{error}</div>}

            <div className="flex flex-wrap gap-2">
              <button className="btn-primary" type="button" disabled={busy || !configured || !userCtx.user} onClick={handleRegister}>
                {busy ? 'Spracovávam...' : 'Povoliť push notifikácie'}
              </button>
              <button className="btn-secondary" type="button" disabled={busy || !subscription || !userCtx.user} onClick={handleUnregister}>
                Zrušiť registráciu
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserAccessNotifications;
